import {useEffect, useRef, useState, useCallback} from 'react';
import {callHardReset} from "../utils/call";
import {CHANNELS, TRANSCRIPTION_RATE} from "../constants/transcription";

export const RTC_CONFIGURATION = {
    iceServers: [
        {urls: 'stun:stun.l.google.com:19302'},
        {urls: 'stun:stun1.l.google.com:19302'},
        {
            urls: 'turn:openrelay.metered.ca:80',
            username: 'openrelayproject',
            credential: 'openrelayproject',
        },
    ],
    iceCandidatePoolSize: 10,
};

export const useWebRTC = (roomCode, userId, onRemoteTrack, sendMessage) => {
    const [status, setStatus] = useState('disconnected');
    const pcRef = useRef(null);
    const localStreamRef = useRef(null);
    const hasSetRemoteDescription = useRef(false);
    const iceCandidatesQueue = useRef([]);

    // Use refs for callbacks to prevent re-renders
    const onRemoteTrackRef = useRef(onRemoteTrack);
    const sendMessageRef = useRef(sendMessage);
    const callSessionIdRef = useRef(0);

    // Update refs when callbacks change
    useEffect(() => {
        onRemoteTrackRef.current = onRemoteTrack;
    }, [onRemoteTrack]);

    useEffect(() => {
        sendMessageRef.current = sendMessage;
    }, [sendMessage]);

    console.log('[WebRTC] Hook initialized', {
        roomCode,
        userId,
        status,
        hasSendMessage: !!sendMessage,
        hasOnRemoteTrack: !!onRemoteTrack
    });

    useEffect(() => {
        console.log('[WebRTC] Component mounted');

        return () => {
            console.log('[WebRTC] Component unmounting, cleaning up...');

            // Stop local stream
            if (localStreamRef.current) {
                localStreamRef.current.getTracks().forEach(t => t.stop());
                localStreamRef.current = null;
            }

            // Close peer connection
            if (pcRef.current) {
                pcRef.current.close();
                pcRef.current = null;
            }
        };
    }, []);

    const createPeerConnection = useCallback(async () => {
        console.log('[WebRTC] 🔧 Creating peer connection...');

        try {
            // Get microphone access
            console.log('[WebRTC] 🎤 Requesting microphone...');
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    sampleRate: TRANSCRIPTION_RATE,
                    channelCount: CHANNELS,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    latency: 0.01,                  // Low latency for real-time
                    sampleSize: 16,
                }
            });
            console.log('[WebRTC] ✅ Microphone granted');
            localStreamRef.current = stream;

            // Create peer connection
            const pc = new RTCPeerConnection(RTC_CONFIGURATION);
            pcRef.current = pc;
            console.log('[WebRTC] ✅ Peer connection created');

            // Add audio tracks
            stream.getTracks().forEach(track => {
                console.log('[WebRTC] Adding track:', track.kind);
                pc.addTrack(track, stream);
            });

            // Handle remote track
            pc.ontrack = (e) => {
                console.log('[WebRTC] ✅ Remote track received:', e.track.kind);
                if (e.streams[0] && onRemoteTrackRef.current) {
                    onRemoteTrackRef.current(e.streams[0]);
                }
            };

            // Handle ICE candidates
            pc.onicecandidate = (e) => {
                if (e.candidate) {
                    console.log('[WebRTC] Generated ICE candidate');
                    sendMessageRef.current({
                        type: 'candidate',
                        candidate: e.candidate.candidate,
                        sdpMid: e.candidate.sdpMid,
                        sdpMLineIndex: e.candidate.sdpMLineIndex,
                    });
                } else {
                    console.log('[WebRTC] ICE gathering complete');
                }
            };

            // Connection state handlers
            pc.oniceconnectionstatechange = () => {
                const state = pc.iceConnectionState;
                console.log('[WebRTC] ICE connection state:', state);

                if (state === 'connected' || state === 'completed') {
                    setStatus('connected');
                } else if (state === 'failed') {
                    setStatus('failed');
                } else if (state === 'disconnected' || state === 'closed') {
                    setStatus('disconnected');
                }
            };

            pc.onconnectionstatechange = () => {
                console.log('[WebRTC] Connection state:', pc.connectionState);
            };

            pc.onsignalingstatechange = () => {
                console.log('[WebRTC] Signaling state:', pc.signalingState);
            };

            console.log('[WebRTC] ✅ Peer connection setup complete');
            return pc;

        } catch (error) {
            console.error('[WebRTC] ❌ Error creating peer connection:', error);
            setStatus('failed');
            throw error;
        }
    }, []);

    const teardown = useCallback((reason = 'local_end') => {
        console.log('[WebRTC] 🔌 Tearing down call...');

        const pc = pcRef.current;
        const stream = localStreamRef.current;

        // Stop local tracks
        if (stream) {
            stream.getTracks().forEach(track => {
                track.stop();
                console.log('[WebRTC] Stopped track:', track.kind);
            });
            localStreamRef.current = null;
        }

        // Close peer connection
        if (pc) {
            pc.getSenders().forEach(sender => {
                try {
                    pc.removeTrack(sender);
                } catch (e) {
                    console.warn('[WebRTC] Error removing track:', e);
                }
            });

            pc.ontrack = null;
            pc.onicecandidate = null;
            pc.oniceconnectionstatechange = null;
            pc.onconnectionstatechange = null;
            pc.onsignalingstatechange = null;

            pc.close();
            pcRef.current = null;
            console.log('[WebRTC] Peer connection closed');

            callHardReset(reason);
        }

        callSessionIdRef.current += 1;
        hasSetRemoteDescription.current = false;
        iceCandidatesQueue.current = [];

        setStatus('disconnected');
        console.log('[WebRTC] ✅ Teardown complete');
    }, []);

    const handleWebRTCMessage = useCallback((message) => {
        console.log('[WebRTC] 📩 Received message:', message.type);

        if (
            message.session_id !== undefined &&
            message.session_id !== callSessionIdRef.current
        ) {
            console.log('[WebRTC] ⏭️ Ignoring stale message', message.type);
            return;
        }

        // Handle call ended
        if (message.type === 'call_ended') {
            console.log('[WebRTC] Call ended by remote peer');
            teardown();
            return;
        }

        (async () => {
            try {
                let pc = pcRef.current;

                // ✅ If receiving offer and no PC exists, create one
                if (message.type === 'offer' && !pc) {
                    console.log('[WebRTC] 📞 Received offer, creating peer connection...');
                    pc = await createPeerConnection();
                    setStatus('connecting');
                }

                if (message.type === 'candidate' && !pc) {
                    console.log('[WebRTC] ⏭️  Received candidate but no peer connection, ignoring');
                    return;
                }

                if (!pc) {
                    console.warn('[WebRTC] ⚠️  No peer connection available');
                    return;
                }

                if (message.type === 'offer') {
                    console.log('[WebRTC] Processing offer...');
                    console.log('[WebRTC] Current signaling state:', pc.signalingState);

                    // Handle non-stable state
                    if (pc.signalingState !== 'stable') {
                        console.warn('[WebRTC] Not in stable state, rolling back...');
                        try {
                            await pc.setLocalDescription({type: 'rollback'});
                        } catch (e) {
                            console.warn('[WebRTC] Rollback failed:', e);
                        }
                    }

                    console.log('[WebRTC] Setting remote description (offer)...');
                    await pc.setRemoteDescription(new RTCSessionDescription({
                        type: 'offer',
                        sdp: message.sdp
                    }));
                    hasSetRemoteDescription.current = true;
                    console.log('[WebRTC] ✅ Remote description set');

                    // Process queued ICE candidates
                    console.log('[WebRTC] Processing', iceCandidatesQueue.current.length, 'queued candidates');
                    for (const c of iceCandidatesQueue.current) {
                        await pc.addIceCandidate(new RTCIceCandidate(c));
                    }
                    iceCandidatesQueue.current = [];

                    console.log('[WebRTC] Creating answer...');
                    const answer = await pc.createAnswer();
                    await pc.setLocalDescription(answer);
                    console.log('[WebRTC] ✅ Answer created, sending...');

                    sendMessageRef.current({
                        type: 'answer',
                        sdp: answer.sdp
                    });
                    console.log('[WebRTC] ✅ Answer sent');

                } else if (message.type === 'answer') {
                    console.log('[WebRTC] Processing answer...');
                    console.log('[WebRTC] Current signaling state:', pc.signalingState);

                    if (pc.signalingState !== 'have-local-offer') {
                        console.warn('[WebRTC] Wrong signaling state for answer:', pc.signalingState);
                        return;
                    }

                    console.log('[WebRTC] Setting remote description (answer)...');
                    await pc.setRemoteDescription(new RTCSessionDescription({
                        type: 'answer',
                        sdp: message.sdp
                    }));
                    hasSetRemoteDescription.current = true;
                    console.log('[WebRTC] ✅ Remote description set');

                    // Process queued ICE candidates
                    console.log('[WebRTC] Processing', iceCandidatesQueue.current.length, 'queued candidates');
                    for (const c of iceCandidatesQueue.current) {
                        await pc.addIceCandidate(new RTCIceCandidate(c));
                    }
                    iceCandidatesQueue.current = [];

                } else if (message.type === 'candidate') {
                    console.log('[WebRTC] Received ICE candidate');

                    const candidate = new RTCIceCandidate({
                        candidate: message.candidate,
                        sdpMid: message.sdpMid,
                        sdpMLineIndex: message.sdpMLineIndex,
                    });

                    if (hasSetRemoteDescription.current) {
                        console.log('[WebRTC] Adding ICE candidate immediately');
                        await pc.addIceCandidate(candidate);
                        console.log('[WebRTC] ✅ ICE candidate added');
                    } else {
                        console.log('[WebRTC] Queueing ICE candidate (no remote description yet)');
                        iceCandidatesQueue.current.push(candidate);
                    }
                }

            } catch (error) {
                console.error('[WebRTC] ❌ Error processing message:', error);
                setStatus('failed');
            }
        })();
    }, [createPeerConnection, teardown]);

    const makeCall = useCallback(async () => {
        console.log('[WebRTC] 📞 Making call...');

        callSessionIdRef.current += 1;
        const sessionId = callSessionIdRef.current;

        try {
            setStatus('connecting');

            // Create peer connection if doesn't exist
            let pc = pcRef.current;
            if (!pc) {
                console.log('[WebRTC] No peer connection, creating...');
                pc = await createPeerConnection();
            }

            console.log('[WebRTC] Creating offer...');
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            console.log('[WebRTC] ✅ Offer created');

            sendMessageRef.current({
                type: 'offer',
                sdp: offer.sdp,
            });
            console.log('[WebRTC] ✅ Offer sent');

        } catch (error) {
            console.error('[WebRTC] ❌ Error making call:', error);
            setStatus('failed');
            throw error;
        }
    }, [createPeerConnection]);

    const endCall = useCallback((reason = 'local_end') => {
        console.log('[WebRTC] Ending call locally');
        teardown(reason);
    }, [teardown]);

    return {
        status,
        localStream: localStreamRef.current,
        endCall,
        makeCall,
        handleWebRTCMessage,
    };
};